import { getAnalyticsClient } from "@/features/analytics/setup";
import logger from "@/utils/logger";

const IMAGE_UPLOAD_TYPES = {
  PARAGRAPH_AND_PICTURE: "Paragraph & Picture",
  DIVIDER_AND_PICTURE: "Divider & Picture",
  HEADER_BACKGROUND: "Header Background Image",
  HEADER_LOGO: "Header Logo",
  RICH_TEXT_EDITOR: "Rich Text Editor",
  ABOUT_DETAILS: "About Details",
};

const PAGE_BUILDER_EVENTS = {
  CANVAS_EDITOR_NAVIGATION_BUTTON_CLICKED:
    "Canvas Editor Navigation Button Clicked",
  CANVAS_EDITOR_BLOCK_ADDED: "Canvas Editor Block Added",
  CANVAS_EDITOR_UPGRADE_MODAL_SHOWN: "Canvas Editor Upgrade Modal Shown",
  CANVAS_EDITOR_UPGRADE_MODAL_CLICKED: "Canvas Editor Upgrade Button Clicked",
  CANVAS_EDITOR_VIEWED: "Canvas Editor Viewed",
  CANVAS_IMAGE_UPLOADED: "Canvas Image Uploaded",
  CANVAS_EDITOR_DATA_IMPORTED: "Canvas Editor Data Imported",
  CANVAS_EDITOR_INITIAL_THEME_SELECTED: "Canvas Editor Initial Theme Selected",
  CANVAS_EDITOR_INITIAL_THEME_SELECTOR_SHOWN:
    "Canvas Editor Initial Theme Selector Shown",
  CANVAS_EDITOR_MENU_ITEM_VIEWED: "Canvas Editor Menu Item Viewed",
  CANVAS_EDITOR_PREFERENCE_PANEL_BUTTON:
    "Canvas Editor Preference Panel Button",
  CANVAS_EDITOR_PREVIEWED: "Canvas Editor Previewed",
  REGISTRATION_PAGE_VIEWED: "Registration Page Viewed",
  REGISTER_TICKET_CLICKED: "Register Ticket Clicked",
  CANVAS_EDITOR_SAVED: "Canvas Editor Saved",
  COMMANDBAR_OPENED: "Commandbar opened",
  COMMANDBAR_ACTION_TOGGLED: "Commandbar action toggled",
  CANVAS_EDITOR_SPEAKER_CARD_SETTINGS_VIEWED:
    "Canvas Speaker Card Settings Viewed",
  CANVAS_SPEAKER_CARD_CLICKED: "Canvas Speaker Card Clicked",
  CANVAS_CONTENT_LIBRARY_OPENED: "Canvas content library opened",
  CANVAS_RECORDING_PLAYED: "Canvas Recording Played",
  CANVAS_EMBEDDED_VIDEO_PLAYED: "Canvas Embedded Video Played",
} as const;

type CANVAS_EVENTS =
  // topbar
  | "ch_canvas_back_to_hubs"
  | "ch_canvas_layout_change"
  | "ch_canvas_sharing_menu_click"
  | "ch_canvas_undo"
  | "ch_canvas_redo"
  | "ch_canvas_preview"
  | "ch_canvas_save"
  // context menu
  | "ch_canvas_ctx_menu_tab_change"
  | "ch_canvas_title_change"
  | "ch_canvas_description_change"
  | "ch_canvas_preview_image_change"
  | "ch_canvas_publish"
  | "ch_canvas_link_copied"
  | "ch_canvas_gating_change"
  | "ch_canvas_color_preset_change"
  // block level
  | "ch_canvas_block_add"
  | "ch_canvas_block_remove"
  | "ch_canvas_block_edit_video_url"
  | "ch_canvas_block_edit_video_lib"
  // video library / details panel
  | "ch_library_video_title_update"
  | "ch_library_video_description_update"
  | "ch_library_video_thumbnail_change"
  | "ch_library_video_form_reset_click"
  | "ch_library_video_copy_click"
  | "ch_library_video_share_click"
  | "ch_content_page_open_shared_url"
  | "ch_library_video_source_update";

export type AnalyticsEventName =
  | typeof PAGE_BUILDER_EVENTS[keyof typeof PAGE_BUILDER_EVENTS]
  | CANVAS_EVENTS;

type Event = {
  name: AnalyticsEventName;
  properties?: Record<string, any>;
};

const logProductAnalyticsEvent = async (event: Event) => {
  const analyticsClient = getAnalyticsClient();

  try {
    if (analyticsClient) {
      await analyticsClient.track(event.name, event.properties);
    }
  } catch (error) {
    logger.error(error, {
      details: "error on sending product analytics event",
      event: {
        name: event.name,
      },
    });
  }
};

const identifyUser = async (userId: string) => {
  const analyticsClient = getAnalyticsClient();

  try {
    if (analyticsClient) {
      await analyticsClient.identify(userId);
    }
  } catch (error) {
    logger.error(error, {
      details: "error on identifying user in analytics",
      userId,
    });
  }
};

export {
  logProductAnalyticsEvent,
  identifyUser,
  PAGE_BUILDER_EVENTS,
  IMAGE_UPLOAD_TYPES,
};
